import { FormEvent, useEffect, useState } from "react";
import { ArrowRight, CalendarClock, Eye, EyeOff, History, KeyRound, Mail, MessageCircle, Phone, Save, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { InputFieldset } from "../../ui/inputFieldset";
import { useNavigate } from "react-router-dom";
import { allowOnlyNumbers, removeFullSpecialCharacters } from "utils/commonutils";
import { getLoginUserDetails, updatePassword, updateProfile } from "../../services/ProfileService";
import { FullScreenLoading } from "components/ui/loader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/accordion";

type ProfileErrors = {
  name?: string;
  emailAddress?: string;
};

type PhoneHistoryEntry = {
  userPhoneNumberHistoryId: number;
  previousEmail?: string | null;
  email?: string | null;
  previousMobileNo?: string | null;
  mobileNo?: string | null;
  previousWhatsAppNumber?: string | null;
  whatsAppNumber?: string | null;
  changedAt: string;
  changedBy?: number;
};

type PasswordErrors = {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

type LoginUserDetails = {
  email?: string;
  itsNo?: string;
  mobileNo?: string;
  whatsAppNumber?: string;
  phoneNumberHistory?: PhoneHistoryEntry[];
};

type LoginUserDetailsResponse = {
  success: boolean;
  data?: LoginUserDetails;
};

export function AccountProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { user, setAuthenticatedUser } = useAuth();
  const { setPageHeader } = usePageHeader();
  const [name, setName] = useState(user?.name || "");
  const [emailAddress, setEmailAddress] = useState(user?.email || "");
  const [itsNumber, setItsNumber] = useState(user?.itsNumber || "");
  const [contactNumber, setContactNumber] = useState(user?.mobileNo || "");
  const [whatsAppNumber, setWhatsAppNumber] = useState(user?.whatsAppNumber || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [profileErrors, setProfileErrors] = useState<ProfileErrors>({});
  const [phoneHistory, setPhoneHistory] = useState<PhoneHistoryEntry[]>([]);
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const isProfileUpdateRestricted =
    !user?.isSuperAdmin &&
    user?.tourOperatorId != null &&
    user?.tourOperatorStatus?.trim().toUpperCase() !== "APPROVED";


  useEffect(() => {
    setPageHeader({
      title: "My Profile",
      icon: <UserRound />,
      onBack: () => navigate(-1),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate]);


  // GET THE LOGIN USER DETAIL FOR ITSNO
  useEffect(() => {
    if (!user?.id) return;

    let isActive = true;
    const loadLoginUserDetails = async () => {
      try {
        setLoading(true);
        const response = await getLoginUserDetails() as LoginUserDetailsResponse;
        const details = response.data;

        if (!isActive) return;
        setEmailAddress(details?.email ?? user.email ?? "");
        setItsNumber(details?.itsNo ?? user.itsNumber ?? "");
        setContactNumber(details?.mobileNo ?? user.mobileNo ?? "");
        setWhatsAppNumber(details?.whatsAppNumber ?? user.whatsAppNumber ?? "");
        setPhoneHistory((details?.phoneNumberHistory ?? []).slice(0, 5));
      } catch {
        // Keep the details saved at login if the refresh request cannot be completed.
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadLoginUserDetails();
    return () => {
      isActive = false;
    };
  }, [user?.id, user?.email, user?.itsNumber, user?.mobileNo, user?.whatsAppNumber]);

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (isProfileUpdateRestricted || isSavingProfile) return;
    const errors: ProfileErrors = {};
    if (!name.trim()) {
      errors.name = "Display name is required.";
    }
    if (!emailAddress.trim()) {
      errors.emailAddress = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress.trim())) {
      errors.emailAddress = "Enter a valid email address.";
    }
    setProfileErrors(errors);

    if (!user || Object.keys(errors).length > 0) {
      return;
    }
    try {
      setLoading(true)
      setIsSavingProfile(true);
      const updateResponse = await updateProfile({
        displayName: name.trim(),
        emailAddress: emailAddress.trim(),
        itsNumber,
        mobileNo: contactNumber.trim(),
        whatsAppNumber: whatsAppNumber.trim(),
      });

      const updatedUser = {
        ...user,
        name: name.trim(),
        email: emailAddress.trim(),
        mobileNo: contactNumber.trim(),
        whatsAppNumber: whatsAppNumber.trim(),
      };
      localStorage.setItem("fhims_auth_user", JSON.stringify(updatedUser));
      if (String(user.id).startsWith("local-")) {
        localStorage.setItem(
          `fhims_local_profile_${user.id}`,
          JSON.stringify({ name: updatedUser.name }),
        );
      }
      setAuthenticatedUser(updatedUser);
      const responseHistory = updateResponse?.data?.phoneNumberHistory;
      if (Array.isArray(responseHistory)) {
        setPhoneHistory(responseHistory.slice(0, 5));
      } else {
        const refreshedProfile = await getLoginUserDetails() as LoginUserDetailsResponse;
        setPhoneHistory((refreshedProfile.data?.phoneNumberHistory ?? []).slice(0, 5));
      }
      toast.success("Profile updated successfully");
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      toast.error(message || "Unable to update profile. Please try again.");
    } finally {
      setIsSavingProfile(false);
      setLoading(false)
    }
  };


  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;
    const errors: PasswordErrors = {};
    if (!currentPassword) errors.currentPassword = "Current password is required.";
    if (!newPassword) errors.newPassword = "New password is required.";
    if (!confirmPassword) errors.confirmPassword = "Please confirm your new password.";

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setPasswordErrors({
        newPassword: "Use at least 8 characters, including uppercase, lowercase, and a number.",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordErrors({ confirmPassword: "New password and confirmation do not match." });
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordErrors({ newPassword: "New password must be different from the current password." });
      return;
    }

    setPasswordErrors({});

    try {
      setLoading(true);
      await updatePassword({
        currentPassword,
        newPassword,
        confirmNewPassword: confirmPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully");
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      toast.error(message || "Unable to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="mx-auto w-full space-y-4 p-3 sm:p-1">
      {loading && <FullScreenLoading />}

      <div className="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-blue-50 px-4 py-3">
        <h1 className="text-lg font-semibold text-slate-900">Account Settings</h1>
        <p className="text-sm text-slate-500">Update your personal information and account password.</p>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {/* PROFILE SECTION STARTS */}
        <Card className="border-sky-200 bg-white" style={{ gap: 0 }}>
          <CardHeader className="border-b border-sky-100 p-4">
            <CardTitle className="flex items-center gap-2 text-base text-slate-900">
              <UserRound className="h-5 w-5 text-[#5B9BD5]" /> Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <form noValidate onSubmit={saveProfile} className="space-y-4">
              <div>
                <InputFieldset
                  label="Display Name"
                  required
                  value={name}
                  aria-invalid={Boolean(profileErrors.name)}
                  aria-describedby={profileErrors.name ? "display-name-error" : undefined}
                  onChange={(event) => {
                    setName(removeFullSpecialCharacters(event.target.value));
                    if (profileErrors.name && event.target.value.trim()) {
                      setProfileErrors({});
                    }
                  }}
                />
                {profileErrors.name && <p id="display-name-error" className="mt-1 text-xs text-red-600">{profileErrors.name}</p>}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <InputFieldset
                    label="Email Address"
                    required
                    type="email"
                    value={emailAddress}
                    aria-invalid={Boolean(profileErrors.emailAddress)}
                    aria-describedby={profileErrors.emailAddress ? "email-address-error" : undefined}
                    onChange={(event) => {
                      setEmailAddress(event.target.value);
                      if (profileErrors.emailAddress) {
                        setProfileErrors((errors) => ({ ...errors, emailAddress: undefined }));
                      }
                    }}
                  />
                  {profileErrors.emailAddress && (
                    <p id="email-address-error" className="mt-1 text-xs text-red-600">
                      {profileErrors.emailAddress}
                    </p>
                  )}
                </div>
                <InputFieldset label="ITS Number" value={itsNumber || "Not available"} readOnly className="cursor-not-allowed bg-gray-50 text-gray-500" />
                <div>
                  <InputFieldset
                    label="Contact Number"
                    type="tel"
                    value={contactNumber}
                    onChange={(event) => setContactNumber(allowOnlyNumbers(event.target.value))}
                    placeholder="Enter contact number"
                  />
                </div>
                <div>
                  <InputFieldset
                    label="WhatsApp Number"
                    type="tel"
                    value={whatsAppNumber}
                    onChange={(event) => setWhatsAppNumber(allowOnlyNumbers(event.target.value))}
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={isSavingProfile || isProfileUpdateRestricted} className="bg-[#5B9BD5] text-white hover:bg-[#4a8bc4]">
                  <Save className="mr-2 h-4 w-4" /> {isSavingProfile ? "Saving..." : "Save Profile"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        {/* PROFILE SECTION ENDS */}

        {/* CHANGE PASSWORD SECTION STARTS */}
        <Card className="border-violet-200 bg-white" style={{ gap: 0 }}>
          <CardHeader className="border-b border-violet-100 p-4">
            <CardTitle className="flex items-center gap-2 text-base text-slate-900">
              <KeyRound className="h-5 w-5 text-violet-600" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <form noValidate onSubmit={changePassword} className="space-y-4">
              <div>
                <InputFieldset
                  label="Current Password"
                  required
                  type={showPasswords ? "text" : "password"}
                  value={currentPassword}
                  aria-invalid={Boolean(passwordErrors.currentPassword)}
                  aria-describedby={passwordErrors.currentPassword ? "current-password-error" : undefined}
                  onChange={(event) => {
                    setCurrentPassword(event.target.value);
                    if (passwordErrors.currentPassword) setPasswordErrors((errors) => ({ ...errors, currentPassword: undefined }));
                  }}
                />
                {passwordErrors.currentPassword && <p id="current-password-error" className="mt-1 text-xs text-red-600">{passwordErrors.currentPassword}</p>}
              </div>
                <div>
                  <InputFieldset
                    label="New Password"
                    required
                    type={showPasswords ? "text" : "password"}
                    value={newPassword}
                    aria-invalid={Boolean(passwordErrors.newPassword)}
                    aria-describedby={passwordErrors.newPassword ? "new-password-error" : undefined}
                    onChange={(event) => {
                      setNewPassword(event.target.value);
                      if (passwordErrors.newPassword) setPasswordErrors((errors) => ({ ...errors, newPassword: undefined }));
                    }}
                  />
                  {passwordErrors.newPassword && <p id="new-password-error" className="mt-1 text-xs text-red-600">{passwordErrors.newPassword}</p>}
                </div>
                <div>
                  <InputFieldset
                    label="Confirm New Password"
                    required
                    type={showPasswords ? "text" : "password"}
                    value={confirmPassword}
                    aria-invalid={Boolean(passwordErrors.confirmPassword)}
                    aria-describedby={passwordErrors.confirmPassword ? "confirm-password-error" : undefined}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                      if (passwordErrors.confirmPassword) setPasswordErrors((errors) => ({ ...errors, confirmPassword: undefined }));
                    }}
                  />
                  {passwordErrors.confirmPassword && <p id="confirm-password-error" className="mt-1 text-xs text-red-600">{passwordErrors.confirmPassword}</p>}
                </div>
              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={() => setShowPasswords((value) => !value)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800">
                  {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showPasswords ? "Hide passwords" : "Show passwords"}
                </button>
                <Button type="submit" className="bg-violet-600 text-white hover:bg-violet-700">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Update Password
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        {/* CHANGE PASSWORD SECTION ENDS */}

        {/* PHONE NUMBER HISTORY */}
        <Accordion type="single" collapsible className="overflow-hidden rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white lg:col-span-2">
          <AccordionItem value="phone-history" className="border-0">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#5B9BD5] text-white shadow-sm">
                  <History className="h-4 w-4" />
                </span>
                <span>
                  <span className="block font-semibold text-slate-800">Contact History</span>
                  <span className="block text-xs font-normal text-slate-500">
                    {phoneHistory.length} recent {phoneHistory.length === 1 ? "change" : "changes"}
                  </span>
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-2.5 pb-2.5">
              {phoneHistory.length > 0 ? (
                <div className="max-h-[360px] space-y-1.5 overflow-y-auto border-t border-sky-100 pt-2 pr-1 sm:max-h-[420px]">
                  {phoneHistory.map((entry) => (
                    <div
                      key={entry.userPhoneNumberHistoryId}
                      className="rounded-lg border border-slate-100 bg-white px-2.5 py-2 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="space-y-1.5">
                        <div className="grid grid-cols-[20px_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5 text-xs sm:grid-cols-[20px_minmax(0,1fr)_auto_minmax(0,1fr)_auto]">
                          <Phone className="h-4 w-4 text-sky-600" />
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide text-slate-700">Previous contact</span>
                            <span className="font-medium text-slate-600">{entry.previousMobileNo || "—"}</span>
                          </div>
                          <ArrowRight className="h-4 w-4 text-sky-400" />
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide text-slate-700">Updated contact</span>
                            <span className="font-semibold text-sky-700">{entry.mobileNo || "—"}</span>
                          </div>
                          <time className="col-span-4 flex items-center justify-self-end whitespace-nowrap text-[10px] text-slate-700 sm:col-span-1" dateTime={entry.changedAt}>
                            <CalendarClock className="mr-1 h-3 w-3 text-[#5B9BD5]" />
                            {new Date(entry.changedAt).toLocaleString()}
                          </time>
                        </div>
                        <div className="grid grid-cols-[20px_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5 border-t border-slate-100 pt-1.5 text-xs sm:grid-cols-[20px_minmax(0,1fr)_auto_minmax(0,1fr)_auto]">
                          <MessageCircle className="h-4 w-4 text-emerald-600" />
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide text-slate-700">Previous WhatsApp</span>
                            <span className="font-medium text-slate-600">{entry.previousWhatsAppNumber || "—"}</span>
                          </div>
                          <ArrowRight className="h-4 w-4 text-emerald-400" />
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide text-slate-700">Updated WhatsApp</span>
                            <span className="font-semibold text-emerald-700">{entry.whatsAppNumber || "—"}</span>
                          </div>
                          <time className="col-span-4 flex items-center justify-self-end whitespace-nowrap text-[10px] text-slate-700 sm:col-span-1" dateTime={entry.changedAt}>
                            <CalendarClock className="mr-1 h-3 w-3 text-emerald-500" />
                            {new Date(entry.changedAt).toLocaleString()}
                          </time>
                        </div>
                        <div className="grid grid-cols-[20px_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5 border-t border-slate-100 pt-1.5 text-xs sm:grid-cols-[20px_minmax(0,1fr)_auto_minmax(0,1fr)_auto]">
                          <Mail className="h-4 w-4 text-violet-600" />
                          <div className="min-w-0">
                            <span className="block text-[10px] uppercase tracking-wide text-slate-700">Previous email</span>
                            <span className="block truncate font-medium text-slate-600" title={entry.previousEmail || undefined}>{entry.previousEmail || "—"}</span>
                          </div>
                          <ArrowRight className="h-4 w-4 text-violet-400" />
                          <div className="min-w-0">
                            <span className="block text-[10px] uppercase tracking-wide text-slate-700">Updated email</span>
                            <span className="block truncate font-semibold text-violet-700" title={entry.email || undefined}>{entry.email || "—"}</span>
                          </div>
                          <time className="col-span-4 flex items-center justify-self-end whitespace-nowrap text-[10px] text-slate-700 sm:col-span-1" dateTime={entry.changedAt}>
                            <CalendarClock className="mr-1 h-3 w-3 text-violet-500" />
                            {new Date(entry.changedAt).toLocaleString()}
                          </time>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-t border-sky-100 py-5 text-center text-xs text-slate-500">
                  No contact changes recorded yet.
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}
